import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch } from '../../Store/Hooks'
import { showAlert } from '../../Store/Slice/alert.slice'
import { BackButton, Breadcrumbs, MUIBox, MUIButton, MUIField, MUITipsCard } from '../Shared'
import CONSTANTS, {
  BREADCRUMS,
  PAGES_ROUTES,
  REVOKE_ACCESS_FORM_VALIDATION_SCHEMA,
} from '../../Constants'
import {
  combineErrorMessage,
  findKeyByWords,
  getPageTitle,
  validateDateTime,
} from '../../Helpers/Util'
import { BreadcrumsModel } from '../../Models'
import { REVOKE_ACCESS } from '../../Constants/Texts'
import { useForm } from 'react-hook-form'
import { Grid } from '@mui/material'
import MUIDateTimePicker from '../MUIDateTimePicker'
import { REVOKE_ACCESS_API } from '../../Constants/ApiDefinations'
import mainApiService from '../../Services'
import { setLoader } from '../../Store/Slice/loader.slice'
import { yupResolver } from '@hookform/resolvers/yup'
import { TIPS_MODULE } from '../../Helpers/ManageTips'
import { logger } from '../../Helpers'

const AccessRevokeDetail = () => {
  const {
    handleSubmit,
    control,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
    getValues,
    watch,
  } = useForm<any>({
    resolver: yupResolver(REVOKE_ACCESS_FORM_VALIDATION_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      startDateTime: null,
      endDateTime: null,
    },
  })

  const [startDateTime, setStartDateTime] = useState<Date | null>(null)
  const [endDateTime, setEndDateTime] = useState<Date | null>(null)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pageType, id } = useParams()

  useEffect(() => {
    if (pageType === 'create' && !startDateTime) {
      const date = new Date()
      date.setMinutes(date.getMinutes() + 1)
      date.setSeconds(0)
      setStartDateTime(date)
      setValue('startDateTime', date)
    }
  }, [pageType])

  useEffect(() => {
    const fetchData = async () => {
      if (pageType !== 'create' && id) {
        const requestData = REVOKE_ACCESS_API.DETAIL(id)
        const result = await mainApiService(requestData)
        if (result?.responseCode === 200) {
          const { data } = result
          if (data) {
            const { title, description, startDateTime, endDateTime } = data
            setValue('title', title || '')
            setValue('description', description || '')
            setValue('startDateTime', new Date(startDateTime))
            setValue('endDateTime', new Date(endDateTime))
            setStartDateTime(new Date(startDateTime))
            setEndDateTime(new Date(endDateTime))
          }
        } else {
          dispatch(showAlert([true, combineErrorMessage(result), 'error']))
          dispatch(setLoader(false))
        }
      }
    }
    fetchData()
  }, [pageType, id])

  const onSubmit = async (data: any) => {
    if (!startDateTime || !endDateTime) return

    const hasError = validateDateTime(
      setError,
      clearErrors,
      startDateTime,
      endDateTime,
      'startDateTime',
      'endDateTime',
      pageType,
    )

    if (hasError) return

    const payload: any = {
      title: data.title,
      description: data.description,
      startDateTime,
      endDateTime,
    }

    if (pageType === 'edit') {
      payload._id = id
    }

    const apiAction = pageType === 'create' ? REVOKE_ACCESS_API.CREATE : REVOKE_ACCESS_API.UPDATE

    try {
      dispatch(setLoader(true))
      const result = await mainApiService(apiAction(payload))

      if (result?.responseCode === 200) {
        navigate(`/${PAGES_ROUTES.BoltMasterNew}/${PAGES_ROUTES.RevokeAccess}`)
        dispatch(showAlert([true, result?.message, 'success']))
      } else if (
        result?.error?.responseCode === 422 &&
        result?.error?.boltCode === CONSTANTS.BOLT_ERROR_CODE_REVOKE_ACCESS_FROM_VALIDATION
      ) {
        const errorMessage = result?.error?.message
        setError('startDateTime', { type: 'custom', message: errorMessage })
        setError('endDateTime', { type: 'custom', message: errorMessage })
      } else {
        dispatch(showAlert([true, combineErrorMessage(result), 'error']))
      }
    } catch (error) {
      dispatch(showAlert([true, 'Unexpected error occurred.', 'error']))
      logger.error(error)
    } finally {
      dispatch(setLoader(false))
    }
  }

  return (
    <div className={'role__editor pb-120'}>
      <div className='pl-10'>
        <Breadcrumbs
          breadcrums={
            BREADCRUMS.CREATE_REVOKE_ACCESS(
              pageType,
              getPageTitle(pageType as string),
            ) as BreadcrumsModel[]
          }
        />
        <BackButton
          label={`${getPageTitle(pageType as string)} Revoke Access`}
          className='col73'
          handleOnClick={() =>
            navigate(`/${PAGES_ROUTES.BoltMasterNew}/${PAGES_ROUTES.RevokeAccess}`)
          }
        />
      </div>
      <div className='mt-1'>
        <div className='role__editor-wrapper'>
          <div className='role__editor-content p-8'>
            <MUIBox>
              <div className='heading-6 d-flex text-left rolebox__header-padding'>
                {REVOKE_ACCESS.REVOKE_ACCESS_FORM_HEADER}
              </div>
              <div className={'hr'}></div>
              <form onSubmit={handleSubmit(onSubmit)} autoComplete='off' style={{ padding: 20 }}>
                <Grid container columnSpacing={10} rowSpacing={4}>
                  <Grid item xs={12} md={12} lg={12} xl={12}>
                    <MUIField
                      name='title'
                      label='Title'
                      required
                      type='text'
                      control={control as any}
                      error={errors}
                      maxLength={100}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={12}>
                    <MUIField
                      name='description'
                      label='Description'
                      required
                      type='text'
                      control={control as any}
                      error={errors}
                      maxLength={100}
                    />
                  </Grid>
                  <Grid item xs={6} md={6} lg={6} xl={6}>
                    <MUIDateTimePicker
                      type='datetime'
                      name='startDateTime'
                      label='Start Date & Time'
                      error={errors}
                      onChange={(value: Date | null) => {
                        setStartDateTime(value)
                        setValue('startDateTime', value)
                      }}
                      onOpen={() => {
                        if (!startDateTime) {
                          const date = new Date()
                          date.setMinutes(date.getMinutes() + 1)
                          date.setSeconds(0)
                          setStartDateTime(date)
                        }
                      }}
                      value={getValues('startDateTime') || startDateTime}
                      required
                      disablePast={true}
                      minutesStep={1}
                    />
                  </Grid>
                  <Grid item xs={6} md={6} lg={6} xl={6}>
                    <MUIDateTimePicker
                      type='datetime'
                      name='endDateTime'
                      label={'End Date & Time'}
                      error={errors}
                      onChange={(value: Date | null) => {
                        setEndDateTime(value)
                        setValue('endDateTime', value)
                      }}
                      onOpen={() => {
                        if (!endDateTime && startDateTime) {
                          const date = getValues('startDateTime') || new Date()
                          date.setMinutes(date.getMinutes() + 10)
                          date.setSeconds(0)
                          setEndDateTime(date)
                        }
                      }}
                      value={getValues('endDateTime') || endDateTime}
                      required
                      disablePast={true}
                      minutesStep={1}
                    />
                  </Grid>
                </Grid>
                <div className='footer'>
                  <div className='submit-btn-wrapper'>
                    <div className='submit-btn'>
                      <MUIButton
                        size='large'
                        type={'submit'}
                        label={'Submit'}
                        variant='contained'
                        width={150}
                        disabled={
                          !startDateTime || !endDateTime || !watch('title') || !watch('description')
                        }
                      />
                    </div>
                  </div>
                </div>
              </form>
            </MUIBox>
          </div>
          <div className=' p-8  ml-1 '>
            <MUITipsCard
              module={TIPS_MODULE.REVOKE_ACCESS}
              pageType={pageType}
              page={findKeyByWords(`revoke_access ${pageType}`)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccessRevokeDetail
